const BRAZE_IAM_CLOSE_BUTTON_SELECTOR = '.ab-close-button';
const BRAZE_IAM_PATCHED_ATTR = 'data-fhs-a11y-patched';
const BRAZE_IAM_CLOSE_LABEL = 'Close';
const BRAZE_IAM_MODAL_PATCHED_ATTR = 'data-fhs-iam-dialog-patched';
const BRAZE_IAM_STYLE_ID = 'fhs-braze-iam-a11y-styles';

const repositionHandlers = new WeakMap<HTMLButtonElement, () => void>();

/**
 * Braze modals render as role="dialog" with two children:
 * the close button and .ab-message-text (role="article"). VoiceOver then reads
 * "dialog, with 2 items" before the close control label when focus lands on X.
 *
 * We move the close button outside the dialog (still visually aligned via fixed
 * positioning) so focus on X only announces "Close, button".
 */
const injectBrazeCloseButtonStyles = () => {
  if (document.getElementById(BRAZE_IAM_STYLE_ID)) {
    return;
  }

  const style = document.createElement('style');
  style.id = BRAZE_IAM_STYLE_ID;
  style.textContent = `
    .ab-iam-root.v3 > button.ab-close-button[${BRAZE_IAM_PATCHED_ATTR}="true"] {
      box-sizing: content-box;
      background-color: transparent;
      background-size: 15px;
      border: none;
      width: 15px;
      min-width: 15px;
      height: 15px;
      cursor: pointer;
      display: block;
      font-size: 15px;
      line-height: 0;
      padding: 15px;
      z-index: 9021;
      margin: 0;
    }
  `;
  document.head.appendChild(style);
};

const ensureModalId = (modal: HTMLElement) => {
  if (!modal.id) {
    modal.id = `fhs-braze-iam-${Math.random().toString(36).slice(2, 9)}`;
  }
  return modal.id;
};

const ensureHeaderId = (header: HTMLElement) => {
  if (!header.id) {
    header.id = `fhs-braze-iam-header-${Math.random().toString(36).slice(2, 9)}`;
  }
  return header.id;
};

const patchInAppMessageDialog = (modal: HTMLElement) => {
  if (modal.getAttribute(BRAZE_IAM_MODAL_PATCHED_ATTR) === 'true') {
    return;
  }

  modal.setAttribute(BRAZE_IAM_MODAL_PATCHED_ATTR, 'true');
  ensureModalId(modal);

  const header = modal.querySelector<HTMLElement>('.ab-message-header');
  if (header) {
    modal.setAttribute('aria-labelledby', ensureHeaderId(header));
    modal.removeAttribute('aria-label');
  }
  // No header: keep Braze's aria-label (e.g. "Modal Message") so the dialog stays named.

  modal.querySelectorAll<HTMLElement>('.ab-message-text[role="article"]').forEach(messageText => {
    messageText.removeAttribute('role');
  });
};

const repositionCloseButton = (closeButton: HTMLButtonElement, modal: HTMLElement) => {
  const rect = modal.getBoundingClientRect();
  closeButton.style.position = 'fixed';
  closeButton.style.top = `${rect.top}px`;
  closeButton.style.left = 'auto';
  closeButton.style.right = `${Math.max(window.innerWidth - rect.right, 0)}px`;
  closeButton.style.zIndex = '9021';
};

const watchCloseButtonPosition = (
  closeButton: HTMLButtonElement,
  modal: HTMLElement,
  iamRoot: HTMLElement
) => {
  const existingHandler = repositionHandlers.get(closeButton);
  if (existingHandler) {
    return;
  }

  const reposition = () => {
    if (!document.contains(closeButton) || !document.contains(modal)) {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      repositionHandlers.delete(closeButton);
      return;
    }

    if (modal.classList.contains('ab-start-hidden')) {
      return;
    }

    repositionCloseButton(closeButton, modal);
  };

  repositionHandlers.set(closeButton, reposition);

  window.addEventListener('resize', reposition);
  window.addEventListener('scroll', reposition, true);

  const resizeObserver = new ResizeObserver(reposition);
  resizeObserver.observe(modal);

  const mutationObserver = new MutationObserver(reposition);
  mutationObserver.observe(modal, { attributes: true, attributeFilter: ['class', 'style'] });
  mutationObserver.observe(iamRoot, { attributes: true, attributeFilter: ['class', 'style'] });

  reposition();
};

const patchCloseButton = (closeButton: HTMLButtonElement) => {
  if (closeButton.getAttribute(BRAZE_IAM_PATCHED_ATTR) === 'true') {
    return;
  }

  const modal = closeButton.closest<HTMLElement>('.ab-in-app-message');
  const iamRoot = closeButton.closest<HTMLElement>('.ab-iam-root');
  if (!modal || !iamRoot) {
    return;
  }

  patchInAppMessageDialog(modal);
  injectBrazeCloseButtonStyles();

  const modalId = ensureModalId(modal);

  closeButton.setAttribute(BRAZE_IAM_PATCHED_ATTR, 'true');
  closeButton.setAttribute('aria-label', BRAZE_IAM_CLOSE_LABEL);
  closeButton.removeAttribute('role');
  closeButton.setAttribute('aria-controls', modalId);
  closeButton.querySelectorAll('svg').forEach(svg => svg.setAttribute('aria-hidden', 'true'));

  iamRoot.insertBefore(closeButton, modal);
  watchCloseButtonPosition(closeButton, modal, iamRoot);
};

const patchVisibleInAppMessages = (root: ParentNode = document) => {
  root
    .querySelectorAll<HTMLButtonElement>(BRAZE_IAM_CLOSE_BUTTON_SELECTOR)
    .forEach(patchCloseButton);
};

let observerInstalled = false;

/**
 * Observe Braze IAM mounts (including after image-load delay) and patch a11y.
 * Web-only; called from braze-api.web initialize.
 */
export const installBrazeInAppMessageAccessibilityPatch = () => {
  if (typeof document === 'undefined' || observerInstalled) {
    return;
  }
  observerInstalled = true;

  patchVisibleInAppMessages();

  const observer = new MutationObserver(() => {
    patchVisibleInAppMessages();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'aria-label'],
  });
};
